import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import { useReveal } from "@/hooks/useReveal";

type ContentItem =
  | { type: "text"; content: string }
  | { type: "bullets"; items: string[] }
  | { type: "subtitle"; content: string };

type Section = {
  title: string;
  content: ContentItem[];
};

const sections: Section[] = [
  {
    title: "1. Introduction",
    content: [
      { type: "text", content: 'This Privacy Policy explains how Vantory Labs LTD ("Company", "we", "our", or "us") collects, uses, stores, and protects personal information when you access or use this website (the "Site") and any related services (collectively, the "Services").' },
      { type: "text", content: "Registered Address\nVantory Labs LTD\n109-111 Fulham Palace Road\nLondon\nW6 8JA\nUnited Kingdom" },
      { type: "text", content: "Contact Information\nEmail: support@vantorylabs.com\nPhone: +447451257361" },
      { type: "text", content: "We are committed to protecting your privacy and processing personal data responsibly and transparently in accordance with applicable privacy and data protection laws, including the UK GDPR where applicable." },
      { type: "text", content: "By accessing or using the Site, you acknowledge that you have read and understood this Privacy Policy." },
    ],
  },
  {
    title: "2. Information We Collect",
    content: [
      { type: "text", content: "We may collect the following categories of information:" },
      { type: "subtitle", content: "a. Personal Information" },
      { type: "text", content: "Depending on how you interact with the Site, we may collect:" },
      {
        type: "bullets",
        items: [
          "Name",
          "Email address",
          "Phone number",
          "Information submitted through forms, support requests, or communications",
        ],
      },
      { type: "subtitle", content: "b. Technical and Usage Information" },
      { type: "text", content: "We may automatically collect certain technical information, including:" },
      {
        type: "bullets",
        items: [
          "IP address",
          "Browser type and version",
          "Device type and operating system",
          "Language preferences",
          "Pages visited and interactions with the Site",
          "Date, time, and duration of visits",
        ],
      },
      { type: "subtitle", content: "c. Communication Information" },
      { type: "text", content: "When you contact us, we may collect:" },
      {
        type: "bullets",
        items: [
          "Email correspondence",
          "Messages submitted through contact forms",
          "Customer support inquiries and related communications",
        ],
      },
      { type: "subtitle", content: "d. Cookies and Similar Technologies" },
      { type: "text", content: "We may use cookies, analytics tools, and similar technologies to improve the functionality, security, and performance of the Site." },
      { type: "text", content: "Additional information about cookies is provided in Section 9 of this Policy." },
    ],
  },
  {
    title: "3. How We Use Information",
    content: [
      { type: "text", content: "We use collected information for legitimate business and operational purposes, including:" },
      { type: "subtitle", content: "a. Providing and Maintaining Services" },
      {
        type: "bullets",
        items: [
          "Responding to inquiries and requests",
          "Operating and maintaining the Site",
          "Delivering requested services or information",
        ],
      },
      { type: "subtitle", content: "b. Improving the Site" },
      {
        type: "bullets",
        items: [
          "Monitoring usage and performance",
          "Understanding user interactions",
          "Improving functionality and user experience",
        ],
      },
      { type: "subtitle", content: "c. Communications" },
      {
        type: "bullets",
        items: [
          "Responding to support requests",
          "Sending important administrative or service-related notices",
          "Providing customer assistance",
        ],
      },
      { type: "subtitle", content: "d. Security and Legal Compliance" },
      {
        type: "bullets",
        items: [
          "Detecting fraud, abuse, or unauthorized activity",
          "Enforcing our Terms of Use",
          "Complying with legal obligations and regulatory requirements",
        ],
      },
      { type: "subtitle", content: "e. Marketing Communications" },
      { type: "text", content: "Where permitted by law or based on your consent, we may send updates, announcements, or promotional communications." },
      { type: "text", content: "You may unsubscribe or opt out at any time." },
    ],
  },
  {
    title: "4. Legal Basis for Processing",
    content: [
      { type: "text", content: "Depending on the circumstances, we process personal data based on one or more of the following legal grounds:" },
      {
        type: "bullets",
        items: [
          "Your consent",
          "Legitimate business interests",
          "Compliance with legal obligations",
          "Performance of a contract or pre-contractual request",
        ],
      },
    ],
  },
  {
    title: "5. Sharing of Information",
    content: [
      { type: "text", content: "We do not sell personal data." },
      { type: "text", content: "However, we may share information in the following situations:" },
      { type: "subtitle", content: "a. Service Providers" },
      { type: "text", content: "We may share data with trusted third-party providers that assist with:" },
      {
        type: "bullets",
        items: [
          "Website hosting",
          "Analytics services",
          "Technical infrastructure",
          "Customer support",
          "Communication services",
        ],
      },
      { type: "text", content: "These providers may only process information on our behalf and under appropriate confidentiality and security obligations." },
      { type: "subtitle", content: "b. Legal Requirements" },
      { type: "text", content: "We may disclose information if required to:" },
      {
        type: "bullets",
        items: [
          "Comply with applicable laws or legal processes",
          "Respond to lawful governmental requests",
          "Protect rights, property, safety, or security",
        ],
      },
      { type: "subtitle", content: "c. Business Transactions" },
      { type: "text", content: "If the Company is involved in a merger, acquisition, restructuring, or sale of assets, information may be transferred as part of that transaction." },
      { type: "subtitle", content: "d. With Your Consent" },
      { type: "text", content: "We may share information where you have expressly consented to such sharing." },
    ],
  },
  {
    title: "6. Data Retention",
    content: [
      { type: "text", content: "We retain personal information only for as long as reasonably necessary to:" },
      {
        type: "bullets",
        items: [
          "Fulfill the purposes described in this Policy",
          "Comply with legal or regulatory obligations",
          "Resolve disputes",
          "Enforce agreements and policies",
        ],
      },
      { type: "text", content: "Retention periods may vary depending on the nature of the data and applicable legal requirements." },
    ],
  },
  {
    title: "7. Data Security",
    content: [
      { type: "text", content: "We implement commercially reasonable technical and organizational safeguards designed to protect personal information, including:" },
      {
        type: "bullets",
        items: [
          "Secure data storage",
          "Access restrictions and authentication controls",
          "Encrypted transmission where appropriate",
          "Monitoring against unauthorized access or misuse",
        ],
      },
      { type: "text", content: "Despite these measures, no method of electronic transmission or storage can be guaranteed to be completely secure." },
    ],
  },
  {
    title: "8. Your Privacy Rights",
    content: [
      { type: "text", content: "Subject to applicable law, you may have the right to:" },
      {
        type: "bullets",
        items: [
          "Access personal data we hold about you",
          "Request correction of inaccurate information",
          "Request deletion of your data",
          "Restrict or object to certain processing activities",
          "Withdraw consent where processing is based on consent",
          "Request portability of your data",
        ],
      },
      { type: "text", content: "To exercise your rights, please contact us at:" },
      { type: "text", content: "support@vantorylabs.com" },
      { type: "text", content: "We may request verification of your identity before processing certain requests." },
    ],
  },
  {
    title: "9. Cookies and Tracking Technologies",
    content: [
      { type: "text", content: "We use cookies and similar technologies to improve functionality, analyze performance, and enhance user experience." },
      { type: "text", content: "Types of cookies we may use include:" },
      { type: "subtitle", content: "Essential Cookies" },
      { type: "text", content: "Required for the operation and security of the Site." },
      { type: "subtitle", content: "Analytics Cookies" },
      { type: "text", content: "Help us understand how users interact with the Site and improve performance." },
      { type: "subtitle", content: "Functional Cookies" },
      { type: "text", content: "Remember user preferences and settings." },
      { type: "text", content: "Most browsers allow you to control or disable cookies through browser settings. Disabling certain cookies may affect Site functionality." },
    ],
  },
  {
    title: "10. International Data Transfers",
    content: [
      { type: "text", content: "Your information may be processed or stored in countries outside your jurisdiction of residence." },
      { type: "text", content: "Where required by applicable law, we implement appropriate safeguards to protect personal data during international transfers." },
    ],
  },
  {
    title: "11. Children's Privacy",
    content: [
      { type: "text", content: "The Site and Services are not directed to individuals under the age of 16." },
      { type: "text", content: "We do not knowingly collect personal information from children. If we become aware that personal data has been collected from a child without appropriate consent, we will take reasonable steps to delete such information." },
    ],
  },
  {
    title: "12. Third-Party Services",
    content: [
      { type: "text", content: "The Site may contain links to external websites or services operated by third parties." },
      { type: "text", content: "We are not responsible for the privacy practices, content, or policies of third-party services. Users should review the applicable privacy policies of external platforms before interacting with them." },
    ],
  },
  {
    title: "13. Changes to this Privacy Policy",
    content: [
      { type: "text", content: "We may update this Privacy Policy from time to time to reflect changes in legal, technical, or operational requirements." },
      { type: "text", content: 'Updated versions will be posted on this page with a revised "Last updated" date.' },
      { type: "text", content: "Continued use of the Site following updates constitutes acceptance of the revised Policy." },
    ],
  },
  {
    title: "14. Contact Information",
    content: [
      { type: "text", content: "Vantory Labs LTD\n109-111 Fulham Palace Road\nLondon\nW6 8JA\nUnited Kingdom" },
      { type: "text", content: "Email: support@vantorylabs.com\nPhone: +447451257361" },
    ],
  },
];

export default function PrivacyPage() {
  const heroRef = useReveal<HTMLDivElement>();

  return (
    <SiteLayout navbarVariant="solid">
      {/* Hero */}
      <section className="relative h-[320px] md:h-[420px] overflow-hidden bg-ink">
        <div className="absolute inset-0 bg-gradient-to-b from-ink/60 via-ink/40 to-ink" />
        <div
          ref={heroRef}
          className="relative h-full flex flex-col justify-end mx-auto max-w-[1320px] px-6 md:px-10 pb-16"
        >
          <div data-reveal>
            <span className="text-xs uppercase tracking-widest2 text-cream/60 mb-4 block">
              Legal
            </span>
            <h1 className="text-display text-5xl md:text-7xl text-cream leading-[0.95]">
              Privacy <span className="italic font-light">Policy</span>.
            </h1>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="bg-cream py-16 md:py-24">
        <div className="mx-auto max-w-[1320px] px-6 md:px-10">
          <div className="max-w-3xl" data-reveal>
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-xs uppercase tracking-widest2 text-ink/50 hover:text-ink transition-colors mb-10 group"
            >
              <i className="ri-arrow-left-line text-sm group-hover:-translate-x-0.5 transition-transform" />
              Back to Home
            </Link>
            <p className="text-ink/60 text-sm mb-12">
              Last updated: May 21, 2026
            </p>

            <div className="space-y-12">
              {sections.map((s) => (
                <div key={s.title}>
                  <h3 className="text-display text-xl md:text-2xl mb-4">{s.title}</h3>
                  <div className="space-y-3">
                    {s.content.map((item, i) => {
                      if (item.type === "subtitle") {
                        return (
                          <p key={i} className="text-ink font-semibold text-sm md:text-base mt-4">
                            {item.content}
                          </p>
                        );
                      }
                      if (item.type === "bullets") {
                        return (
                          <ul key={i} className="space-y-1.5 pl-1">
                            {item.items.map((bullet, j) => (
                              <li key={j} className="flex items-start gap-2.5 text-ink/70 text-sm md:text-base leading-relaxed">
                                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-ink/40 shrink-0" />
                                {bullet}
                              </li>
                            ))}
                          </ul>
                        );
                      }
                      return (
                        <p key={i} className="text-ink/70 text-sm md:text-base leading-relaxed whitespace-pre-line">
                          {item.content}
                        </p>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
